import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog } from '@mui/material';
import AuthFormCard from '@components/layout/AuthFormCard';
import FamilyOnboardingForm from '@components/profile/FamilyOnboardingForm';
import { useAuthStore } from '@store/authStore';
import authService from '@services/authService';
import { getFamilySetupNextPath, needsFamilyOnboarding } from '@utils/authRouting';

/**
 * Family setup after the dashboard has painted. Does not replace first navigation.
 */
const FamilySetupDialog: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const storedUser = user || authService.getStoredUser();
  const required = needsFamilyOnboarding(storedUser) && location.pathname !== '/family-setup';
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!required) {
      setOpen(false);
      return;
    }
    const t = window.setTimeout(() => setOpen(true), 280);
    return () => window.clearTimeout(t);
  }, [required]);

  if (!required) return null;

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      onClose={() => undefined}
      PaperProps={{
        sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' },
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(17, 23, 57, 0.48)' } },
      }}
      aria-labelledby="family-setup-title"
    >
      <AuthFormCard
        roleLabel="Account"
        title="Your family status"
        subtitle="Set this once. Add a child later only if you are married and have saved your relation."
      >
        <FamilyOnboardingForm
          onSaved={(saved) => {
            setOpen(false);
            navigate(getFamilySetupNextPath(saved), { replace: true });
          }}
        />
      </AuthFormCard>
    </Dialog>
  );
};

export default FamilySetupDialog;
